import { toFormat, isInFuture } from "@/lib/dateHelpers";
import {
  getStatusTagColor,
  getCompletionLabel,
  filterAndSortRequirements
} from "@/features/person/utils/pgm-requirements";
import type { EducationPgmProgram, PgmProgramRequirement } from "@/types";

export type TestGroupKeySource = {
  testId: string;
  testDefinition?: { oldTestId?: string | null } | null;
};

export const getTestGroupKey = (test: TestGroupKeySource): string => {
  if (test.testDefinition?.oldTestId) return test.testDefinition.oldTestId;

  return test.testId.replace(/TEST(?:1|R)$/, "TEST");
};

export const deduplicateTests = (
  tests: NonNullable<EducationPgmProgram["testHistory"]>
): NonNullable<EducationPgmProgram["testHistory"]> => {
  const bestByTestGroup = new Map<string, (typeof tests)[number]>();
  for (const t of tests) {
    const groupKey = getTestGroupKey(t);
    const existing = bestByTestGroup.get(groupKey);
    if (
      !existing ||
      (t.isPassed && !existing.isPassed) ||
      (!existing.isPassed && !t.isPassed && t.attemptNumber > existing.attemptNumber)
    ) {
      bestByTestGroup.set(groupKey, t);
    }
  }
  return Array.from(bestByTestGroup.values());
};

export type ProgramDetailType = "Requirement" | "Test";

export interface ProgramDetailRow {
  id: string;
  type: ProgramDetailType;
  description: string;
  secondaryText: string;
  requireId?: string;
  levelCode: string;
  date: string | null;
  completed: boolean;
  statusLabel: string;
  statusType: "success" | "warn" | "error" | "surface" | "information";
  scoreText?: string | null;
  requirement?: PgmProgramRequirement;
  levelStartDate?: string | null;
}

export interface ProgramProgressRow {
  id: string;
  level: string;
  code: string;
  version: string;
  stage: string;
  startDate: string | null;
  endDate: string | null;
  startDateIso: string;
  endDateIso: string | null;
  completeCode: string | null;
  remarks: string | null;
  detailsCount: number;
  completedDetailsCount: number;
  status: string;
  completed: boolean;
  expiresDate?: string | null;
  isExpired?: boolean;
  details: ProgramDetailRow[];
}

const isQualifyingLevel = (level: number) => level === 0;

export const formatProgramData = (programHistory: EducationPgmProgram[]): ProgramProgressRow[] => {
  return programHistory.map((level, index) => {
    const sortedReqs = level.programRequirements
      ? filterAndSortRequirements(level.programRequirements, level.levelCode)
      : [];

    const requirementRows: ProgramDetailRow[] = sortedReqs.map((req, reqIndex) => {
      const statusLabel = getCompletionLabel(req.isCompleted, req.completionStatus);
      const statusType = getStatusTagColor(statusLabel);
      return {
        id: `${index}-req-${reqIndex}`,
        type: "Requirement" as ProgramDetailType,
        description: req.requirementDefinition?.description || "Unknown requirement",
        secondaryText: req.requireId,
        requireId: req.requireId,
        levelCode: req.levelCode,
        date: req.completionDate ? toFormat(req.completionDate) : null,
        completed: req.isCompleted,
        statusLabel,
        statusType,
        scoreText: req.score != null ? `${req.score}` : null,
        requirement: req,
        levelStartDate: level.startDate
      };
    });

    const levelTests = (level.testHistory || []).filter((t) => t.levelCode === level.levelCode);
    const dedupedTests = deduplicateTests(levelTests);
    const testRows: ProgramDetailRow[] = dedupedTests.map((test, testIndex) => ({
      id: `${index}-test-${testIndex}`,
      type: "Test" as ProgramDetailType,
      description: test.testDefinition?.description || test.testId,
      secondaryText: test.testId,
      levelCode: test.levelCode,
      date: test.testDate ? toFormat(test.testDate) : null,
      completed: test.isPassed,
      statusLabel: test.isPassed ? "Passed" : test.isFailed ? "Failed" : "Not Attempted",
      statusType: test.isPassed ? "success" : test.isFailed ? "error" : "surface",
      scoreText: test.score != null ? `${test.score}/${test.maxScore}` : null
    }));

    const details = [...requirementRows, ...testRows];

    return {
      id: index.toString(),
      level: isQualifyingLevel(level.level)
        ? "Qualifying Level"
        : level.careerPath
          ? `Level ${level.level} ${level.careerPath}`
          : `Level ${level.level}`,
      code: level.levelCode,
      version: level.curriculumVersion,
      stage: level.completed ? "Completed" : "Started",
      startDate: level.startDate ? toFormat(level.startDate) : null,
      endDate: level.endDate ? toFormat(level.endDate) : null,
      startDateIso: level.startDate,
      endDateIso: level.endDate ?? null,
      completeCode: level.completeCode ?? null,
      remarks: level.remarks ?? null,
      detailsCount: details.length,
      completedDetailsCount: details.filter((d) => d.completed).length,
      status: level.completed ? "Complete" : "In Progress",
      completed: level.completed,
      expiresDate: level.levelExpirationDate ? toFormat(level.levelExpirationDate) : null,
      isExpired: level.levelExpirationDate ? !isInFuture(level.levelExpirationDate) : false,
      details
    };
  });
};
